import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  format, addDays, addWeeks, startOfWeek, startOfDay, isBefore, isToday,
} from "date-fns";
import { ChevronLeft, ChevronRight, CalendarCheck, Loader2, Ban } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/lib/services";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const EASE = [0.22, 1, 0.36, 1];
const SLOTS = Array.from({ length: 13 }, (_, i) => `${String(i + 8).padStart(2, "0")}:00`);

const slotLabel = (s) => {
  const h = parseInt(s, 10);
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const EMPTY_FORM = { name: "", phone: "", email: "", address: "", notes: "" };

export default function BookingCalendar({ service, onServiceChange, services = SERVICES }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [availability, setAvailability] = useState({ booked: [], blocked: [], closed: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const thisWeekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 0 }), []);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/availability", {
        params: {
          start: format(weekDates[0], "yyyy-MM-dd"),
          end: format(weekDates[6], "yyyy-MM-dd"),
        },
      });
      setAvailability({ closed: [], ...data });
    } catch (e) {
      toast.error("Couldn't load the calendar — please refresh.");
    } finally {
      setLoading(false);
    }
  }, [weekDates]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const slotKey = (day, slot) => `${format(day, "yyyy-MM-dd")}|${slot}`;
  const isPastSlot = (day, slot) => {
    if (isBefore(day, startOfDay(new Date()))) return true;
    if (isToday(day) && parseInt(slot, 10) <= new Date().getHours()) return true;
    return false;
  };

  const openSlot = (day, slot) => {
    setSelected({ date: format(day, "yyyy-MM-dd"), time: slot });
    setSuccess(false);
    setForm(EMPTY_FORM);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!service) {
      toast.error("Please choose a service first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        service,
        date: selected.date,
        time_slot: selected.time,
      });
      setSuccess(true);
      confetti({
        particleCount: 90,
        spread: 75,
        origin: { y: 0.6 },
        colors: ["#1E4B33", "#65A30D", "#84CC16", "#ECFCCB"],
      });
      toast.success("Booking request sent! We'll confirm shortly.");
      fetchAvailability();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't send your booking. Please try again."));
      if (err?.response?.status === 409) fetchAvailability();
    } finally {
      setSubmitting(false);
    }
  };

  const prettySelected = selected
    ? `${format(new Date(`${selected.date}T12:00:00`), "EEEE, MMMM d")} · ${slotLabel(selected.time)}`
    : "";

  return (
    <section id="booking" data-testid="booking-calendar-section" className="py-24 bg-sage/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-6 mb-10"
        >
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-limed mb-4">
              Schedule Work
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-emerald-950 leading-[1.08]">
              Grab your slot on the crew calendar.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-emerald-900/70 leading-relaxed">
              Pick a day and time, Sunday through Saturday. Greyed-out slots are already taken or
              not offered that day.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="booking-prev-week"
              onClick={() => setWeekStart((w) => addWeeks(w, -1))}
              disabled={!isBefore(thisWeekStart, weekStart)}
              className="p-2.5 rounded-full border border-emerald-950/15 bg-white text-pine disabled:opacity-30 hover:border-limed transition-colors duration-200"
              aria-label="Previous week"
            >
              <ChevronLeft size={18} />
            </button>
            <div data-testid="booking-week-label" className="text-center min-w-[180px]">
              <p className="font-display font-bold text-emerald-950">
                {format(weekDates[0], "MMM d")} – {format(weekDates[6], "MMM d, yyyy")}
              </p>
              <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-900/50">
                Week of Sunday
              </p>
            </div>
            <button
              data-testid="booking-next-week"
              onClick={() => setWeekStart((w) => addWeeks(w, 1))}
              disabled={isBefore(addWeeks(thisWeekStart, 8), addWeeks(weekStart, 1))}
              className="p-2.5 rounded-full border border-emerald-950/15 bg-white text-pine disabled:opacity-30 hover:border-limed transition-colors duration-200"
              aria-label="Next week"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="bg-white rounded-3xl border border-emerald-950/10 shadow-xl shadow-emerald-950/5 p-4 sm:p-6"
        >
          <div className="overflow-x-auto">
            <div className="min-w-[820px]">
              <div data-testid="booking-day-selector" className="grid grid-cols-7 gap-2 mb-3">
                {weekDates.map((day) => (
                  <div
                    key={day.toISOString()}
                    data-testid={`booking-day-header-${format(day, "yyyy-MM-dd")}`}
                    className={`text-center rounded-2xl py-2.5 ${
                      isToday(day) ? "bg-pine text-bone" : "bg-sage/60 text-emerald-950"
                    }`}
                  >
                    <p className="text-[11px] font-mono uppercase tracking-widest opacity-70">
                      {format(day, "EEE")}
                    </p>
                    <p className="font-display font-bold text-lg leading-tight">{format(day, "d")}</p>
                  </div>
                ))}
              </div>

              <div className={`grid grid-cols-7 gap-2 transition-opacity duration-300 ${loading ? "opacity-40" : ""}`}>
                {weekDates.map((day) => (
                  <div key={day.toISOString()} className="flex flex-col gap-1.5">
                    {SLOTS.map((slot) => {
                      const key = slotKey(day, slot);
                      const taken = availability.booked.includes(key);
                      const blocked = availability.blocked.includes(key);
                      const closed = availability.closed.includes(key);
                      const past = isPastSlot(day, slot);
                      const disabled = taken || blocked || closed || past;
                      return (
                        <button
                          key={key}
                          data-testid={`booking-slot-btn-${key}`}
                          disabled={disabled}
                          onClick={() => openSlot(day, slot)}
                          className={`relative rounded-xl py-2 text-xs font-semibold transition-all duration-200 ${
                            disabled
                              ? "bg-slate-100 text-slate-400 line-through cursor-not-allowed"
                              : "bg-limed-light/60 text-pine hover:bg-limed hover:text-pine-dark hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                          }`}
                          title={
                            taken ? "Already booked"
                            : blocked ? "Unavailable"
                            : closed ? "Not offered this day"
                            : past ? "Past"
                            : "Available"
                          }
                        >
                          {taken || blocked || closed ? (
                            <span className="inline-flex items-center gap-1">
                              <Ban size={10} />
                              {slotLabel(slot)}
                            </span>
                          ) : (
                            slotLabel(slot)
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-5 text-xs font-mono uppercase tracking-widest text-emerald-900/50">
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-limed-light border border-limed/40" /> Available
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-slate-200" /> Taken / Unavailable
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-pine" /> Today
            </span>
          </div>
        </motion.div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="sm:max-w-md bg-bone" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-emerald-950">
              {success ? "You're on the list!" : "Book this slot"}
            </DialogTitle>
            <DialogDescription className="text-emerald-900/60">{prettySelected}</DialogDescription>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {success ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: EASE }}
                data-testid="booking-success-message"
                className="text-center py-6"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-limed-light flex items-center justify-center text-limed mb-4">
                  <CalendarCheck size={30} />
                </div>
                <p className="font-semibold text-emerald-950">Booking request received.</p>
                <p className="mt-2 text-sm text-emerald-900/60 leading-relaxed">
                  We'll confirm your slot shortly by text or email. Need us sooner? Call or text
                  816-400-9576.
                </p>
                <button
                  data-testid="booking-done-btn"
                  onClick={() => setSelected(null)}
                  className="mt-6 bg-pine text-bone font-semibold px-6 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors duration-300"
                >
                  Done
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={submitBooking}
                className="flex flex-col gap-3.5"
              >
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">
                    Service
                  </label>
                  <select
                    data-testid="booking-service-dropdown"
                    value={service || ""}
                    onChange={(e) => onServiceChange(e.target.value)}
                    required
                    className="w-full rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm text-forest focus:outline-none focus:ring-2 focus:ring-limed/50"
                  >
                    <option value="" disabled>
                      Choose a service
                    </option>
                    {services.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name} — {s.price}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="booking-form-name"
                    required
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                  />
                  <input
                    data-testid="booking-form-phone"
                    required
                    placeholder="Phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                  />
                </div>
                <input
                  data-testid="booking-form-email"
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                />
                <input
                  data-testid="booking-form-address"
                  required
                  placeholder="Job address (Oak Grove / Grain Valley)"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                />
                <textarea
                  data-testid="booking-form-notes"
                  placeholder="Notes for the crew (optional)"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50 resize-none"
                />
                <button
                  data-testid="booking-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="mt-1 inline-flex items-center justify-center gap-2 bg-pine text-bone font-semibold px-6 py-3 rounded-full hover:bg-limed hover:text-pine-dark transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? "Sending..." : "Request This Slot"}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
}
